import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarPlus, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, API } from "@/lib/api";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";
import { CalendarFeed } from "@/components/CalendarFeed";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const AddToCalendar = ({ item, compact }) => (
  <span className="inline-flex items-center gap-2">
    <a href={item.google_url} target="_blank" rel="noopener noreferrer" data-testid={`gcal-${item.id}`}
      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline">
      <CalendarPlus className="h-3.5 w-3.5" /> {compact ? "Google" : "Google Calendar"}
    </a>
    <a href={`${API}/proceedings/${item.id}/calendar.ics`} data-testid={`ics-${item.id}`}
      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline">
      <Download className="h-3.5 w-3.5" /> .ics
    </a>
  </span>
);

export default function Calendar() {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = useState(key(new Date()));

  useEffect(() => {
    api.get("/calendar").then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const byDay = useMemo(() => {
    const m = {};
    (items || []).forEach((it) => { const k = key(new Date(it.scheduled_at)); (m[k] = m[k] || []).push(it); });
    return m;
  }, [items]);

  const cells = useMemo(() => {
    const first = new Date(cursor);
    const lead = (first.getDay() + 6) % 7;
    const start = new Date(first); start.setDate(1 - lead);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const shift = (n) => setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() + n); return d; });
  const today = key(new Date());
  const dayItems = byDay[selected] || [];
  const upcoming = (items || []).filter((it) => it.status !== "cancelled" && new Date(it.scheduled_at) >= new Date()).slice(0, 8);

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Diary" title="Hearing Calendar"
        description="Every sitting posted by the tribunal on your matters. Add any sitting to Google Calendar or download it for Outlook and Apple Calendar."
        testId="calendar-header"
        actions={
          <a href={`${API}/calendar.ics`} data-testid="calendar-ics-all"
            className="inline-flex items-center h-10 px-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
            <Download className="h-3.5 w-3.5 mr-2" /> All sittings (.ics)
          </a>
        } />

      {!items ? <div className="h-96 border border-border bg-muted animate-pulse" /> : (
        <div className="grid gap-8 lg:grid-cols-3">
          <section className="lg:col-span-2 border border-border bg-card" data-testid="calendar-grid">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <button onClick={() => shift(-1)} data-testid="calendar-prev" className="p-2 hover:bg-accent transition-colors duration-200"><ChevronLeft className="h-4 w-4" /></button>
              <p className="font-display text-xl" data-testid="calendar-month-label">
                {cursor.toLocaleString("en-IN", { month: "long", year: "numeric" })}
              </p>
              <button onClick={() => shift(1)} data-testid="calendar-next" className="p-2 hover:bg-accent transition-colors duration-200"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-7 border-b border-border">
              {DAYS.map((d) => <div key={d} className="p-2 text-center label-caps">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((d) => {
                const k = key(d), inMonth = d.getMonth() === cursor.getMonth(), list = byDay[k] || [];
                return (
                  <button key={k} onClick={() => setSelected(k)} data-testid={`calendar-day-${k}`}
                    className={`min-h-20 sm:min-h-24 p-1.5 text-left border-b border-r border-border transition-colors duration-200 hover:bg-accent/60 ${
                      !inMonth ? "text-muted-foreground/50 bg-muted/30" : ""} ${selected === k ? "bg-blue-50 dark:bg-blue-950/40" : ""}`}>
                    <span className={`inline-flex h-6 w-6 items-center justify-center text-xs ${k === today ? "bg-blue-900 text-white rounded-full" : ""}`}>{d.getDate()}</span>
                    <div className="mt-1 space-y-0.5">
                      {list.slice(0, 2).map((it) => (
                        <p key={it.id} className={`truncate text-[10px] px-1 ${it.status === "cancelled" ? "line-through bg-slate-300 text-slate-600 dark:bg-slate-700 dark:text-slate-400" : "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"}`} data-testid={`calendar-chip-${it.id}`}>
                          {new Date(it.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} {it.case_number || it.ref_id}
                        </p>
                      ))}
                      {list.length > 2 && <p className="text-[10px] text-muted-foreground px-1">+{list.length - 2} more</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="space-y-8">
            <section className="border border-border bg-card p-5" data-testid="calendar-day-panel">
              <p className="label-caps">{new Date(`${selected}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
              {dayItems.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3" data-testid="calendar-day-empty">No sittings on this day.</p>
              ) : dayItems.map((it) => (
                <div key={it.id} className="mt-4 border-l-2 border-blue-900 pl-3" data-testid={`calendar-day-item-${it.id}`}>
                  <Link to={`/cases/${it.case_id}`} className="font-mono text-xs text-blue-900 dark:text-blue-400 hover:underline">{it.case_number || it.ref_id}</Link>
                  <p className="text-sm font-medium mt-0.5">{it.purpose}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(it.scheduled_at)} · {it.mode}{it.venue ? ` · ${it.venue}` : ""}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <Badge className={it.status === "cancelled" ? "bg-red-50 text-red-900" : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"}>{it.status}</Badge>
                    {it.status !== "cancelled" && <AddToCalendar item={it} compact />}
                  </div>
                </div>
              ))}
            </section>

            <section className="border border-border bg-card p-5" data-testid="calendar-upcoming">
              <p className="label-caps">Next sittings</p>
              {upcoming.length === 0 ? <EmptyState testId="calendar-upcoming-empty" title="Nothing scheduled" hint="Sittings posted by the tribunal appear here." /> :
                upcoming.map((it) => (
                  <div key={it.id} className="mt-3 flex items-start justify-between gap-3 text-sm" data-testid={`calendar-upcoming-${it.id}`}>
                    <div>
                      <p className="font-medium">{it.purpose}</p>
                      <p className="text-xs text-muted-foreground">{fmtDateTime(it.scheduled_at)} · {it.case_number || it.ref_id}</p>
                    </div>
                    <button onClick={() => { setSelected(key(new Date(it.scheduled_at))); setCursor(() => { const d = new Date(it.scheduled_at); d.setDate(1); return d; }); }}
                      className="text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline whitespace-nowrap">View</button>
                  </div>
                ))}
            </section>

            <CalendarFeed />
          </aside>
        </div>
      )}
    </div>
  );
}
